import _taggedTemplateLiteralLoose from "@babel/runtime/helpers/esm/taggedTemplateLiteralLoose";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n\t\t\t", "\n\t\t\t", "\n\t\t\t", "\n\t\t\t", "\n\t\t"]);

  _templateObject = function () {
    return data;
  };

  return data;
}

import { mergeRefs } from '@wp-g2/utils';
import { repeat } from 'lodash';
import React, { useRef } from 'react';
import { DARK_HIGH_CONTRAST_MODE_MODE_ATTR, DARK_MODE_ATTR, HIGH_CONTRAST_MODE_MODE_ATTR, MODE_SPECIFICITY_COMPOUND_LEVEL } from '../../create-style-system/constants';
import { useHydrateGlobalStyles } from '../../hooks';
import { ThemeProviderContext, useThemeProviderContextBridge, useThemeProviderModeHtmlAttributes } from './theme-provider-context';
import { useColorBlindMode, useDarkMode, useHighContrastMode, useReducedMotionMode, useThemeStyles } from './utils';
/**
 * @typedef ThemeProviderProps
 * @property {import('react').ReactNode} children Children to render.
 * @property {import('../../create-compiler').Compiler} compiler The style compiler.
 * @property {string} className Optional className to render on the provider.
 * @property {boolean} isGlobal Determines if the theme styles are rendered globally or scoped locally.
 * @property {import('../../create-style-system/generate-theme').GenerateThemeResults} globalStyles Styles to apply globally.
 * @property {boolean} isDark Determines if dark-mode styles should be rendered.
 * @property {boolean} isColorBlind Determines if color-blind-mode styles should be rendered.
 * @property {boolean} isReducedMotion Determines if reduced-motion-mode styles should be rendered.
 * @property {boolean} isHighContrast Determines if high-contrast-mode styles should be rendered.
 * @property {Record<string, string>} theme Custom theme properties.
 * @property {Record<string, string>} darkTheme Custom dark theme properties.
 * @property {Record<string, string>} highContrastTheme Custom high contrast theme properties.
 * @property {Record<string, string>} darkHighContrastTheme Custom dark & high contrast theme properties.
 */

/**
 * The ThemeProvider for the Style system. This ThemeProvider uses Emotion's
 * ThemeProvider as a foundation, but enhances it with features provided by
 * the Style system, such as dark mode, high contrast mode, etc...
 *
 * An important feature this ThemeProvider accounts for is the ability to render
 * styles either globally (at the document/html level) or scoped.
 *
 * @example
 * ```jsx
 * <ThemeProvider isGlobal theme={{...}}>
 *   <Button>...</Button>
 * </ThemeProvider>
 * ```
 *
 * @param {ThemeProviderProps} props Props for the ThemeProvider.
 * @returns {JSX.Element} Children content wrapped with the <ThemeProvider />.
 */

function ThemeProvider(_ref, forwardedRef) {
  var children = _ref.children,
      compiler = _ref.compiler,
      className = _ref.className,
      _ref$isGlobal = _ref.isGlobal,
      isGlobal = _ref$isGlobal === void 0 ? false : _ref$isGlobal,
      globalStyles = _ref.globalStyles,
      isDark = _ref.isDark,
      isColorBlind = _ref.isColorBlind,
      isReducedMotion = _ref.isReducedMotion,
      isHighContrast = _ref.isHighContrast,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? {} : _ref$theme,
      _ref$darkTheme = _ref.darkTheme,
      darkTheme = _ref$darkTheme === void 0 ? {} : _ref$darkTheme,
      _ref$highContrastThem = _ref.highContrastTheme,
      highContrastTheme = _ref$highContrastThem === void 0 ? {} : _ref$highContrastThem,
      _ref$darkHighContrast = _ref.darkHighContrastTheme,
      darkHighContrastTheme = _ref$darkHighContrast === void 0 ? {} : _ref$darkHighContrast,
      props = _objectWithoutProperties(_ref, ["children", "compiler", "className", "isGlobal", "globalStyles", "isDark", "isColorBlind", "isReducedMotion", "isHighContrast", "theme", "darkTheme", "highContrastTheme", "darkHighContrastTheme"]);

  var css = compiler.css,
      cx = compiler.cx,
      injectGlobal = compiler.injectGlobal;
  /**
   * Hydrates global styles (via injectGlobal). This is necessary as there may
   * be a chance that <ThemeProvider /> renders before any other (styled)
   * component. Injecting global styles early in this manner allows for
   * the initial render of theme styles (which also uses injectGlobal)
   * to be sequenced correctly.
   */

  useHydrateGlobalStyles({
    injectGlobal: injectGlobal,
    globalStyles: globalStyles
  });
  var nodeRef = useRef();
  var defaultStyles = useThemeStyles({
    injectGlobal: injectGlobal,
    isGlobal: isGlobal,
    theme: theme,
    selector: ':root'
  });
  var darkStyles = useThemeStyles({
    injectGlobal: injectGlobal,
    isGlobal: isGlobal,
    theme: darkTheme,
    selector: repeat(DARK_MODE_ATTR, MODE_SPECIFICITY_COMPOUND_LEVEL)
  });
  var highContrastStyles = useThemeStyles({
    injectGlobal: injectGlobal,
    isGlobal: isGlobal,
    theme: highContrastTheme,
    selector: repeat(HIGH_CONTRAST_MODE_MODE_ATTR, MODE_SPECIFICITY_COMPOUND_LEVEL)
  });
  var darkHighContrastStyles = useThemeStyles({
    injectGlobal: injectGlobal,
    isGlobal: isGlobal,
    theme: darkHighContrastTheme,
    selector: repeat(DARK_HIGH_CONTRAST_MODE_MODE_ATTR, MODE_SPECIFICITY_COMPOUND_LEVEL)
  });
  useColorBlindMode({
    isColorBlind: isColorBlind,
    isGlobal: isGlobal,
    ref: nodeRef
  });
  useDarkMode({
    isDark: isDark,
    isGlobal: isGlobal,
    ref: nodeRef
  });
  useHighContrastMode({
    isGlobal: isGlobal,
    isHighContrast: isHighContrast,
    ref: nodeRef
  });
  useReducedMotionMode({
    isGlobal: isGlobal,
    isReducedMotion: isReducedMotion,
    ref: nodeRef
  });
  var contextState = useThemeProviderContextBridge({
    isDark: isDark,
    isReducedMotion: isReducedMotion,
    isColorBlind: isColorBlind,
    isHighContrast: isHighContrast
  });
  var modeHtmlAttrs = useThemeProviderModeHtmlAttributes(contextState);
  var classes = cx(className, css(_templateObject(), defaultStyles, darkStyles, highContrastStyles, darkHighContrastStyles));
  return /*#__PURE__*/React.createElement("div", Object.assign({}, props, modeHtmlAttrs, {
    className: classes,
    "data-system-theme-provider": true,
    ref: mergeRefs([forwardedRef, nodeRef])
  }), /*#__PURE__*/React.createElement(ThemeProviderContext.Provider, {
    value: contextState
  }, children));
}

export default /*#__PURE__*/React.memo( /*#__PURE__*/React.forwardRef(ThemeProvider));